import { cn } from "@/lib/utils";

// The shape the ported screens' form controls share: MUI's outlined field in
// its two sizes, and the menu that drops out of one.
//
// The donor theme overrides none of `MuiSelect`, `MuiOutlinedInput`,
// `MuiMenuItem` or `MuiAutocomplete`, so **MUI's defaults are the source spec**
// (`docs/agents/migration-guidelines.md`, "Shared donor components port as
// shared Atlas components"). Four components in this directory draw that
// shape — the sort/page-size select, the category tree select, the
// manufacturer autocomplete, and the search field — and the values below are
// why they agree:
//
//   - a control 40px tall at MUI's `small` and 56px at its default, with 4px
//     corners, white, 16px text and 14px of side padding either way,
//   - a 1px outline that becomes a 2px focus-blue one drawn inward, with the
//     insets measured from it taking the pixel back, so nothing inside or beside
//     the control shifts when it opens (`MUI_FIELD_FRAME_BORDER_PUBLISHED`),
//   - a menu at the trigger's own width with 8px of list padding and rows that
//     run edge to edge — 36px tall, 16px text, tinted rather than ticked when
//     current.
//
// Class strings rather than a component, because the four controls are four
// different things (a Radix Select, a Popover, an autocomplete, a plain input)
// and only their *look* is shared. A component would have to be all four.
//
// ## A MUI default is not a donor fact until you have checked what disables it
//
// This file is the shared home for MUI-derived measurements, so the rule that
// governs reading one off `@mui/material` lives here. "The donor overrides no
// `MuiX`, so MUI's default is the spec" has a step before it: **the default has
// to apply to the donor's theme in the first place.**
//
// `letterSpacing` is the case that caught us (#453). `createTypography` emits it
// only when the theme's font family is MUI's own Roboto default:
//
//     ...(fontFamily === defaultFontFamily ? { letterSpacing: … } : {})
//     — createTypography.js:56-58
//
// The donor sets `fontFamily: InterFontFamily` (`'Inter', sans-serif`;
// `theme.ts:5,123`), so **every** built variant — `body1`, `body2`, `button`,
// `caption`, `subtitle2`, and the headings — reaches the donor with no
// letter-spacing at all. Four ported values were copied out of MUI's Roboto
// table and shipped tracking the donor never draws.
//
// So: read the value out of MUI, then check the donor theme for what *turns it
// off* — not just for what overrides it. The check is one step earlier in the
// chain than "did the donor override this?".
//
// The corollary, which cost #453 a review round: with the theme's own
// letter-spacing gone, **every** remaining one is declared by some individual
// component, so it has to be traced to that component rather than assumed from a
// sibling. `theme.ts` declares three (h1/h2/h4); the rest are scattered across
// `timmy/src` and `timmy/packages` — `navigation-tabs.tsx:8` on the header's tab
// bar, `table.styles.ts:61` on the shared table's container, the env indicator's
// CSS module, and a dozen more on unported screens. Two surfaces that render the
// same MUI component need not agree: the header's tabs are tracked because
// `NavigationTabs` tracks them, while the Admin page's bare `<Tabs>`
// (`admin-page.tsx:137-141`) is not tracked at all. Grep the source for
// `letterSpacing` per surface; do not generalise one screen's answer to the next.

/**
 * MUI's placeholder: the input's own ink at 42%.
 *
 * Every donor input is an `InputBase`, which draws `::placeholder` in
 * `color: currentColor` at `opacity: 0.42` in the light palette
 * (`InputBase.js:126-136`), and the donor's theme overrides neither. Tailwind's
 * preflight mixes the ink at 50% instead — a visibly darker grey beside the
 * source (#535). Spelled exactly as MUI spells it, the ink plus an opacity,
 * rather than as a hex, because MUI's number is an opacity on whatever colour
 * the input is, disabled ink included. And not as `text-current/42` either:
 * that compiles to a `color-mix()` on `currentcolor`, which crashes Safari
 * 16.4–16.5 — the reason preflight guards its own such rule — while an opacity
 * needs no guard anywhere.
 *
 * Carried on the field shape rather than as a global `::placeholder` rule so
 * only the ported MUI inputs take it — the vendored `Input` and `Command`, on
 * Atlas-native screens with no donor, keep their own. Exported for a ported
 * input that does not compose `MUI_FIELD_BASE`: `SearchField`'s sits *inside*
 * a shaped box, whose own class cannot reach its child's placeholder, and
 * `TextField` builds its own control — it takes no `placeholder` today, so the
 * day it grows one is the day it composes this.
 */
export const MUI_FIELD_PLACEHOLDER = "placeholder:text-current placeholder:opacity-[0.42]";

/**
 * **The width of an outline drawn as a real `border`, published for the insets
 * measured from it.**
 *
 * MUI never stacks an outline and a padding on one box. `.MuiInputBase-root` is
 * `border-width: 0`, and the visible 1px is a child `<fieldset>` at `left: 0;
 * right: 0` that draws it *inside* its own box
 * (`OutlinedInput/NotchedOutline.js:19-33`) — so the root's padding box and
 * border box coincide, and every inset MUI states is measured from the edge a
 * user sees: the input's 14px either side (`OutlinedInput.js:88-112`), the
 * select's 32px gutter (`NativeSelectInput.js:80,93`), the caret's 7px
 * (`:140,147`), the autocomplete's 6 + 8 and 39 + 4 (`Autocomplete.js:139-167`).
 *
 * Atlas draws the outline as a `border` on the same box that carries the
 * padding, and under `border-box` the padding starts *inside* the border — so
 * each of those numbers, written as-is, landed a pixel further in: text at 15
 * and 33 where the donor measures 14 and 32 (#576, item G). Rather than restate
 * every number as its unsourced neighbour (13, 31, 6, 42), the shape that draws
 * the border also publishes its width here, and each inset is written as MUI's
 * number less this variable. The numbers stay the donor's; the correction is
 * one declaration beside the border it corrects for.
 *
 * Read with a `0px` fallback wherever the reader may sit on a box that draws no
 * border of its own — the select gutter and the caret inset (`mui-select-caret.tsx`)
 * serve frame-owned triggers too, the `border-0` ones inside an `OutlinedField`,
 * and there the variable is simply unset.
 *
 * It inherits, which is what lets the caret — a child of the trigger — read the
 * trigger's value. Nothing wearing this shape contains another field, so the
 * inheritance reaches nothing it should not.
 */
const MUI_FIELD_FRAME_BORDER_PUBLISHED = "[--mui-field-frame-border:1px]";

/**
 * MUI's 14px of side padding, from the visible edge: the published border width
 * and the one inset that every field shape takes from it, which travel together.
 */
const MUI_FIELD_SIDE_PADDING = cn(
  MUI_FIELD_FRAME_BORDER_PUBLISHED,
  "px-[calc(14px-var(--mui-field-frame-border))]",
);

/**
 * The outlined field minus its height: 4px corners, white, 16px text, 14px of
 * side padding from the visible edge (`MUI_FIELD_FRAME_BORDER_PUBLISHED`), an
 * outline that becomes the focus blue, and MUI's placeholder ink
 * (`MUI_FIELD_PLACEHOLDER`).
 *
 * **The second focus pixel is drawn inward, as the donor's is.** MUI thickens
 * the fieldset's own border from 1px to 2px (`NotchedOutline.js:23,30`;
 * `OutlinedInput.js:61-63`), entirely inside the legend's cut — measured on the
 * running donor's item edit page, a focused Material trigger reads
 * `border-width: 2px` with its legend at 10px, against 1px/9px resting. Atlas
 * spells it the same way — the border goes to 2px — and the frame's published
 * width goes to 2px with it (`MUI_FIELD_FRAME_BORDER_PUBLISHED`), so every
 * inset measured from the visible edge takes the pixel back and nothing in the
 * control moves by one, as nothing moves in MUI, whose fieldset is out of flow.
 *
 * It was a 1px border plus a 1px inset shadow until #576's parity review, which
 * painted the same two pixels by two means and so antialiased badly wherever
 * the box landed on a fractional pixel: the two soft edges overlapped in the
 * middle device pixel, which composited lighter than either. Measured on the
 * open Sort trigger, 64,109,153 where the donor's line is solid 0,60,119. One
 * border is one paint source, the way MUI's is.
 *
 * It was `ring-1` until #579 (D3), and Tailwind paints a ring as an *outset*
 * shadow, at y = -1. The notch was then a white band starting at the border
 * box's own top edge, so it could not cover a line drawn above that edge: the
 * focus line ran straight through the label on every popover-backed control. Since #846 a
 * control with a label notch does not paint this line at all: it keeps the
 * geometry (`MUI_FIELD_BASE_GEOMETRY`) and `FieldNotch`'s fieldset draws the
 * line over it, with the hole cut (`MUI_FIELD_NOTCHED_TRIGGER`).
 *
 * **`focus:`, not `focus-visible:`.** MUI's `.Mui-focused` answers focus
 * however it arrived, and the controls wearing this shape are `<button>`s,
 * where `:focus-visible` does not match a mouse press. Measured on the running
 * donor: a `Select` dismissed with Escape keeps its 2px blue line while the
 * trigger still holds focus, because `SelectInput` swallows the blur for as
 * long as its menu is open and the focus comes straight back
 * (`SelectInput.js:549-561`). Under `focus-visible:` the port dropped to the
 * resting grey there. A text input matches `:focus-visible` on a mouse click
 * anyway, so the two spellings only ever disagreed on the buttons.
 */
/**
 * **`MUI_FIELD_BASE` without its paint** — every box, inset and width, and no
 * colour on the line.
 *
 * Split out for the controls whose line `FieldNotch` draws (#846): a trigger
 * with a label notch keeps this box exactly — the 1px border going to 2px on
 * focus or an open menu, and the published width with it, so the text, caret
 * and gutter hold still — but wears it transparent, and the fieldset laid over
 * it paints the line with the hole cut out (`MUI_FIELD_OUTLINE_PEER`).
 * `focus:outline-none` lives here because both kinds need the browser's own
 * ring gone.
 */
const MUI_FIELD_BASE_GEOMETRY = cn(
  "text-lennar-ink rounded-[4px] border bg-white text-base shadow-none focus:outline-none",
  MUI_FIELD_PLACEHOLDER,
  MUI_FIELD_SIDE_PADDING,
  // The thickness, for every field that is focused or open — the refused ones
  // included, which is why it stands outside the `not-aria-invalid:` colour
  // rules: MUI's error replaces the colour and never the thickness
  // (`OutlinedInput.js:61-63,76-81`). The published width moves with it.
  "focus:border-2 focus:[--mui-field-frame-border:2px]",
  "data-[state=open]:border-2 data-[state=open]:[--mui-field-frame-border:2px]",
  "disabled:text-lennar-ink-disabled disabled:opacity-100",
);

const MUI_FIELD_BASE = cn(
  MUI_FIELD_BASE_GEOMETRY,
  "border-lennar-field-outline",
  // MUI's outline precedence, the same four states `OutlinedField` paints on
  // its frame (`OutlinedInput.js:53-82`) — a select trigger is an
  // `OutlinedInput` too, so it wears them all: hover darkens the resting 23%
  // line to near-black, focus and open replace it with the primary, and
  // disabled replaces the lot with `action.disabled`.
  //
  // **Disabled is restated, not faded.** `opacity-50` on the trigger would wash
  // its ground grey, and the donor theme forces white on every `FormControl`
  // (`theme.ts:385-391`) — so the fade is a screen the source never draws.
  // `not-data-[state=open]` because the hover rule outranks the open one on
  // specificity, not order — `:enabled:hover` is three pseudo-classes against
  // `[data-state=open]`'s two — so hovering an *open* select repainted its
  // outline the resting near-black. MUI's `focused` beats its `hover` in the
  // cascade; here the hover rule has to stand down. A trigger with no
  // `data-state` at all (the autocomplete's input, the tree's search box) still
  // matches, so it keeps its hover.
  // `not-focus:` for the same reason `not-data-[state=open]:` is there,
  // one state along: MUI emits its focused colour as a variant *after* the
  // `:hover` root rule at equal specificity, so focus beats hover
  // (`OutlinedInput.js:53-82`). Here the hover selector carries three
  // pseudo-classes and the focus rule one, so without this the pointer
  // repainted a focused field's outline back to the resting near-black.
  //
  // `not-aria-invalid:` is the third of the same shape, and it stands down
  // **all three** of the states below — hover, focus, and the open menu.
  // MUI emits `&.Mui-error .notchedOutline` last of the lot, in a variant
  // whose own comment says it is there "to override the above style"
  // (`OutlinedInput.js:76-81`), so a refused field is one solid 2px
  // `error.main` line in every state: under the pointer, under the caret, and
  // with its menu down. Second pixel included — error replaces the colour,
  // never the thickness.
  //
  // It lives here rather than on each caller because a caller cannot win it
  // back. These selectors carry more pseudo-classes than a bare `focus:` or
  // `data-[state=open]:` override, so the stand-down belongs to the rule that
  // has to stand down. Every field wearing this shape marks its refusal with
  // `aria-invalid`, which is what makes one variant enough for all of them,
  // and a field that grows an error colour later inherits the fix rather than
  // rediscovering the bug — which is exactly how `multi-select-field` came to
  // be the one call site still painting a blue pixel inside a red line, and
  // `tree-select` to go blue outright once its menu took the focus away.
  "enabled:hover:not-data-[state=open]:not-focus:not-aria-invalid:border-lennar-field-outline-hover",
  "focus:not-aria-invalid:border-lennar-blue-600",
  "data-[state=open]:not-aria-invalid:border-lennar-blue-600",
  // The thickness is the geometry's (`MUI_FIELD_BASE_GEOMETRY`); only the
  // colour is restated here.
  "disabled:border-lennar-action-disabled",
);

/**
 * The element standing in for MUI's `.MuiOutlinedInput-root` — the one holding
 * the input and its adornments, and nothing else.
 *
 * Its own export, named as a pair with {@link MUI_FIELD_OUTLINE_HOVER_WITHIN},
 * because that rule is silently dead without this class on the right element
 * and nothing in the type system can say so. Two names that must ship together are
 * at least greppable from each other.
 */
export const MUI_FIELD_CONTROL_ROOT = "group/control";

/**
 * The refusal colour for anything wearing `MUI_FIELD_BASE`.
 *
 * MUI does not have a separate "errored and focused" appearance: `.Mui-error`
 * recolours the one `notchedOutline` and the focused rule still supplies the
 * second pixel, so a refused field reads as a single 2px `error.main` line in
 * every state (`OutlinedInput.js:61-63` for the thickness, `:76-81` for the
 * colour). That is why this restates the *colour* in each state and never the
 * geometry — the 2px width is the base's, and it applies here unchanged.
 *
 * One constant rather than a string per call site because the four surfaces
 * that can be refused had drifted into three different spellings of it, and
 * the one still on the pre-#579 spelling (`focus-visible:`) painted a blue
 * pixel inside its red line on every mouse press — `:focus-visible` does not
 * match a pressed `<button>`, which is the whole premise the surrounding
 * shape is built on. A shared constant is what makes that undetectable by
 * drift rather than by inspection.
 *
 * The `data-[state=open]` clause is inert on the surfaces that have no menu
 * (the rich-text link box is a bare `<input>`); it costs a class and saves a
 * caller from having to know whether it needs one.
 */
export const MUI_FIELD_ERROR = cn(
  "border-lennar-error",
  "focus:border-lennar-error",
  "data-[state=open]:border-lennar-error",
);

/** MUI's outlined `size='small'` control: 40px. */
export const MUI_FIELD_TRIGGER = cn(MUI_FIELD_BASE, "h-10");

/**
 * **A trigger whose line `FieldNotch` draws** — `MUI_FIELD_TRIGGER`'s box,
 * worn transparent, and marked as the `peer` the fieldset after it reads.
 *
 * Every trigger with a floating label keeps the label's hole cut rather than
 * painted (#846), so its line is the fieldset's, not its own border's. The
 * border stays, at the same 1px going to 2px, because everything inside the
 * control is inset from it (`MUI_FIELD_FRAME_BORDER_PUBLISHED`); only its
 * colour goes. Render `<FieldNotch className={MUI_FIELD_OUTLINE_PEER} />` as a
 * later sibling, inside a positioned box the size of this control.
 */
export const MUI_FIELD_NOTCHED_TRIGGER = cn(
  MUI_FIELD_BASE_GEOMETRY,
  "peer border-transparent h-10",
);

/**
 * {@link MUI_FIELD_NOTCHED_TRIGGER} at MUI's **default** size — `medium`, 56px.
 *
 * Two sizes because the donor uses two, and which one a control gets is a fact
 * about its call site rather than a house style: the items sidebar passes
 * `size='small'` to every field it draws (`items-filter-sidebar.tsx:173-291`),
 * while a form field takes MUI's default and stands 56px tall — the mark-core
 * dialog's tier select (`mark-core-item-dialog.tsx:151`), `CategoriesFieldPaginated`,
 * every `InputField` in the Request Item dialog. Drawing one of those at 40px is
 * a 16px control in a 56px context — the gap reads immediately beside the source.
 */
export const MUI_FIELD_NOTCHED_TRIGGER_MEDIUM = cn(
  MUI_FIELD_BASE_GEOMETRY,
  "peer border-transparent h-14",
);

/**
 * **`MUI_FIELD_BASE`'s paint, moved onto the fieldset** — the line's states,
 * read from the control before it through Tailwind's `peer-*`.
 *
 * The same five states in MUI's precedence (`OutlinedInput.js:53-82`), each
 * setting `MUI_FIELD_OUTLINE`'s two custom properties rather than a border:
 * resting `rgba(0,0,0,0.23)` at 1px; the pointer's near-black, standing down
 * for focus, an open menu and a refusal; the primary at 2px for focus or an
 * open menu; `error.main` for a refusal, which replaces the colour and never
 * the width; and `action.disabled`, which replaces the lot. The resting pair is
 * set here rather than left to the fallbacks, so that a field inside another
 * field's frame — the Link URL box inside the Description editor — never
 * inherits the outer frame's state.
 *
 * **Every rule but the resting pair carries `peer-enabled:`.** MUI's disabled
 * replaces the lot, and its `FormControl` drops focus the moment it is
 * disabled, so a trigger disabled with its menu still open (a save starting)
 * is the 1px `action.disabled` line. Here the rules would not yield on their
 * own: the open and focus colours carry a `not-aria-invalid` the disabled rule
 * does not, so they outrank it, and the width is a second property the
 * disabled rule never sets. `aria-invalid` and `:disabled` tie, and the order
 * Tailwind sorts its variants into would decide that one. Scoping each rule to
 * an enabled control says so outright, whatever the specificity.
 */
export const MUI_FIELD_OUTLINE_PEER = cn(
  "[--mui-outline-color:var(--color-lennar-field-outline)] [--mui-outline-width:1px]",
  "peer-enabled:peer-hover:peer-not-data-[state=open]:peer-not-focus:peer-not-aria-invalid:[--mui-outline-color:var(--color-lennar-field-outline-hover)]",
  "peer-enabled:peer-focus:peer-not-aria-invalid:[--mui-outline-color:var(--color-lennar-blue-600)]",
  "peer-enabled:peer-data-[state=open]:peer-not-aria-invalid:[--mui-outline-color:var(--color-lennar-blue-600)]",
  "peer-enabled:peer-focus:[--mui-outline-width:2px] peer-enabled:peer-data-[state=open]:[--mui-outline-width:2px]",
  "peer-enabled:peer-aria-invalid:[--mui-outline-color:var(--color-lennar-error)]",
  "peer-disabled:[--mui-outline-color:var(--color-lennar-action-disabled)]",
);

/**
 * `MUI_FIELD_OUTLINE_PEER`'s hover, re-sourced from an ancestor marked
 * `group/control`.
 *
 * MUI hangs the hover on `.MuiOutlinedInput-root` — the wrapper that holds the
 * input, the adornments *and* the fieldset — so anything the root contains
 * darkens the whole frame: `&:hover .notchedOutline { borderColor:
 * text.primary }` (`OutlinedInput.js:53-58`). Atlas has no root element; the
 * peer rule reads the input itself, and a bare `peer-hover:` therefore only
 * fires for the pointer over the input's own box.
 *
 * That is invisible until a field puts a real control inside its frame. The
 * autocomplete's popup indicator is an `IconButton` in the donor, so porting
 * it faithfully laid an interactive 28px sibling over the last of a 250px
 * control — and the frame stopped darkening exactly where the user aims to
 * open the list, while the donor's goes near-black. Measured on both stacks:
 * donor `rgba(0, 0, 0, 0.87)` with the caret hovered, Atlas still the resting
 * `rgba(0, 0, 0, 0.23)`.
 *
 * Carries the same three stand-downs as the rule it mirrors, for the same
 * reason they are on that rule: focus, an open menu and a refusal each outrank
 * the pointer, and a copy that dropped one would out-specify the state it is
 * supposed to yield to.
 *
 * **The group has to be MUI's root, not the whole field.** `:hover` propagates
 * from any descendant, including one painted outside the ancestor's own box —
 * and a floated `MUI_FIELD_LABEL` is exactly that, sitting 7px *above* the
 * frame and `pointer-events: auto`. Hung on the field wrapper, this rule
 * therefore darkened the outline from a label the donor cannot darken it from:
 * MUI's `InputLabel` is a *sibling* of `.MuiOutlinedInput-root` under the
 * `FormControl`, so it is not inside the hover's scope at all. Mark the element
 * that stands in for that root — the one holding the input and its adornments,
 * and nothing else — with {@link MUI_FIELD_CONTROL_ROOT}.
 *
 * The fieldset sits inside that group, as MUI's sits inside
 * `.MuiOutlinedInput-root`, and reads the three stand-downs off the `peer`.
 *
 * @see MUI_FIELD_OUTLINE_PEER, whose three stand-downs this repeats. They are
 * spelled out rather than shared: Tailwind v4 scans source text, so a modifier
 * prefix assembled from a constant generates no CSS.
 */
export const MUI_FIELD_OUTLINE_HOVER_WITHIN =
  "group-hover/control:peer-enabled:peer-not-data-[state=open]:peer-not-focus:peer-not-aria-invalid:[--mui-outline-color:var(--color-lennar-field-outline-hover)]";

/**
 * The same control at MUI's **default** size — 56px rather than the small
 * field's 40px (`padding: 16.5px 14px` around a 23px line) — and shaped as the
 * *box around* an input rather than as the input itself, because a field with
 * adornments is a row: icon, text, button.
 *
 * `has-[input:focus]` rather than `focus-visible`, since the focusable element
 * is the `<input>` inside this box and the outline belongs to the box. It is
 * spelled out rather than prefixed onto `MUI_FIELD_TRIGGER` at a call site for
 * the reason this file keeps repeating: Tailwind v4 scans source text, so a
 * variant built by interpolation generates no CSS.
 *
 * **A press on the outline itself blurs the input for a moment — an
 * owner-ruled standing parity break** (#576 decision round, D2 = c,
 * 2026-09-22). MUI's line is a `pointer-events: none` fieldset laid over a
 * root whose input fills its full height, so a press on the top or bottom
 * 1–2px lands on the `<input>` and the field keeps its focus. Here the line is
 * this box's own border, so the same press lands on the box: the input blurs
 * and the box's click handler focuses it again (`search-field.tsx`). Nothing
 * visible changes, but `blur` and `focus` fire. Accepted rather than reworked:
 * matching it means drawing the line as an overlay on every field wearing this
 * shape. Anyone tempted to re-report it should read this block first.
 */
export const MUI_FIELD_BOX_MEDIUM = cn(
  "border-lennar-field-outline text-lennar-ink flex h-14 w-full items-center rounded-[4px] border bg-white text-base shadow-none",
  // MUI's `adornedStart` / `adornedEnd` 14px (`OutlinedInput.js:88-97`), from
  // the visible edge — the same 1px correction `MUI_FIELD_BASE` takes, for the
  // same stacked border. The donor's magnifier measures 14px in; this box's
  // measured 15 (#576, item G).
  MUI_FIELD_SIDE_PADDING,
  // MUI's hover, `&:hover .notchedOutline { borderColor: text.primary }`
  // (`OutlinedInput.js:53-55`), standing down while focused — MUI emits the
  // focused colour after it. This box had no hover at all (#576's parity
  // review).
  "hover:not-has-[input:focus]:border-lennar-field-outline-hover",
  // The focused 2px `MUI_FIELD_BASE` carries, for the box-shaped sibling: one
  // border, thickened inward, with the published width moving alongside so
  // the magnifier and the text hold still.
  //
  // **Keyed on the input's focus, not `focus-within`.** MUI's `focused` is
  // the `<input>`'s own (`InputBase`'s focus handlers), so tabbing on to an
  // end-adornment button drops the field to its resting 1px; `focus-within`
  // kept the blue 2px while the clear button held the focus.
  "has-[input:focus]:border-lennar-blue-600 has-[input:focus]:border-2 has-[input:focus]:[--mui-field-frame-border:2px]",
);

/**
 * The geometry every popover paper shares, with the elevation left out.
 *
 * Separated because the elevation is the one part that is *not* shared: MUI's
 * `Menu` paper is elevation 8 and the `Autocomplete`'s is elevation 1, and in
 * this theme those are not even the same kind of thing — see
 * `MUI_AUTOCOMPLETE_PANEL`.
 */
const MUI_PAPER_SHAPE = "min-w-[var(--radix-popper-anchor-width)] rounded-[4px] p-0";

/**
 * The margin MUI's `Popover` keeps between a panel and every viewport edge.
 *
 * `marginThreshold` defaults to 16 (`Popover.js:111`) and the clamp at
 * `:199-219` slides an overflowing panel back inside by exactly the overflow:
 * left of the margin it moves right, past `innerWidth - margin` it moves left,
 * and the same two branches run on the vertical. Radix's `collisionPadding`
 * defaults to **0**, so before this every Atlas panel opened at an edge sat
 * flush against it — measured on the two stacks at 1440x900, the category
 * menu's right edge read 1440.2 against the donor's 1424.2 (#632).
 *
 * **The horizontal is exact; the vertical is not, and cannot be from here.**
 * Radix feeds this padding to `shift({ mainAxis: true, crossAxis: false })`
 * and to `flip()` (`@radix-ui/react-popper/dist/index.mjs:106-112`), so a
 * panel that will not fit below its trigger *flips above it* where MUI slides
 * it up over the trigger and never changes side. `shift`'s cross axis is not
 * a prop Radix exposes, so matching that would mean replacing the positioning
 * layer rather than configuring it. Not approximated with a max-height
 * either: MUI explicitly does **not** shrink a tall panel — it logs "The
 * popover component is too tall" and lets it overflow (`Popover.js:211-214`).
 *
 * **Owner-ruled a standing divergence** (#542 decision round, D4,
 * 2026-09-22): the flip ships, and this is its record. Measured cost is 17px
 * of panel top on the category menu — modsy 384, Atlas 401. Anyone tempted to
 * re-report it should read this block first; the fix is a positioning-layer
 * replacement across every menu in the app, which is not what the symptom is
 * worth.
 */
export const MUI_POPOVER_MARGIN_THRESHOLD = 16;

/**
 * MUI's `Menu` paper: 4px corners, one elevation.
 *
 * **No vertical padding**, and that is load-bearing rather than an omission.
 * MUI puts the 8px on the `MenuList` *inside* the scrolling paper, so a search
 * box pinned with `position: sticky; top: 0` pins flush to the paper's own
 * edge. Put the same 8px on the scroll container instead and the sticky box
 * pins 8px *below* the top — leaving a band that scrolled rows paint through,
 * above a search box that is supposed to hide them. `MUI_MENU_LIST` is where
 * the padding lives now.
 */
export const MUI_MENU_SURFACE = cn(
  MUI_PAPER_SHAPE,
  // **No border — elevation is the whole treatment.** MUI's `Paper` draws
  // `1px solid divider` only under `variant: 'outlined'`
  // (`Paper/Paper.js:56-71`), and a `Menu`'s paper is `elevation`: every donor
  // menu measures `border-width: 0px`. The vendored `SelectContent` and
  // `PopoverContent` both carry a bare `border`, and this constant overrode
  // their radius and shadow but not that, so every ported menu wore a 1px grey
  // rim under its drop shadow — the whole of the 2px the Category menu
  // measured wider than the donor's (#576, item F). `MUI_AUTOCOMPLETE_PANEL`
  // takes the same `border-0` for the same vendored reason.
  "border-0",
  // **MUI's elevation 8, spelled out.** Every menu on a ported screen is a
  // `Popover`, whose elevation defaults to 8 (`Popover.js:110`) and which no
  // call site or theme entry here overrides — the two that pass `slotProps` set
  // only `maxHeight`. Tailwind's `shadow-lg` is a lighter shadow thrown further
  // down the page, and six menus on the edit screen alone wore it.
  "shadow-[0px_5px_5px_-3px_rgba(0,0,0,0.2),0px_8px_10px_1px_rgba(0,0,0,0.14),0px_3px_14px_2px_rgba(0,0,0,0.12)]",
);

/**
 * MUI's `MenuList`: the 8px that used to sit on the paper, inside the scroll
 * area where a sticky header can pin above it.
 *
 * **Vertical only, and every call site owns whether it applies.** Two of them
 * have already been bitten in opposite directions, so the trap reads from both
 * sides:
 *
 * - `menu-select.tsx` (the lookup select) **zeroes** it. Its menu ends in a
 *   sticky "Add" row, and the donor's list has no bottom padding under one
 *   (`CustomSelectWithPagination.tsx:221-225`); carrying the 8px left a strip
 *   below the row that scrolled options painted through. Owner-ruled on #412.
 * - `autocomplete-field.tsx` **needs** it, and for a different reason than the
 *   menus: its listbox slot is replaced in the donor
 *   (`AutocompletePaginated.tsx:106` → `listbox.tsx:11` renders a plain MUI
 *   `<List>`), so the 8px there is `List`'s own default rather than
 *   `MenuList`'s. Same number, different source — which is why it is applied
 *   at the call site and not folded into `MUI_AUTOCOMPLETE_PANEL`.
 */
export const MUI_MENU_LIST = "py-2";

/**
 * The category menu's height cap — **the donor's own number**, and now shared.
 *
 * `CategoriesFieldPaginated.tsx:281` sets `slotProps.paper.sx = { maxHeight:
 * '500px' }`, so this is a fact about the source rather than a taste.
 *
 * It is a named constant, not a repeated literal, because the autocomplete's
 * paper takes it *by derivation*: the owner's 2026-09-22 ruling asked for a
 * panel that scrolls "similar to the Category filter", which is a reference to
 * this cap and not to the number 500. If the category menu's cap ever moves,
 * the autocomplete's has to follow it, and a copied literal would not.
 *
 * Contrast `MUI_MENU_LIST`, which stays at its call site precisely because
 * *its* 8px is the same number from a different source. Same-number-different-
 * source stays apart; derived-from stays together.
 */
export const MUI_MENU_PANEL_MAX_HEIGHT = "max-h-[500px]";

/**
 * The paper, sized the way MUI sizes a **`Select`'s** menu rather than an
 * `Autocomplete`'s.
 *
 * The difference is real and visible: MUI's `Autocomplete` popper takes the
 * input's width, but a `Select`'s menu takes the *trigger's width as a
 * minimum* and then grows to fit its longest row, capped at the viewport. In a
 * 250px sidebar that is the whole point — "Domestic Water Filtration
 * Equipment" is a legible row in the source and would be an ellipsis at
 * trigger width. The donor constrains only the height
 * (`CategoriesFieldPaginated.tsx:281`, `slotProps.paper.sx = { maxHeight:
 * '500px' }`), leaving the width to MUI.
 */
export const MUI_SELECT_MENU_PANEL = cn(
  MUI_MENU_SURFACE,
  MUI_MENU_PANEL_MAX_HEIGHT,
  "w-max max-w-[calc(100vw-2rem)] overflow-y-auto",
  "min-w-[var(--radix-popover-trigger-width)]",
);

/**
 * The **`Autocomplete`'s** paper, which is a different elevation *and* a
 * different kind of shadow from every menu above.
 *
 * `AutocompletePaper` takes the default `elevation={1}`
 * (`Autocomplete.js:299-306`), and the donor theme replaces `shadows[1]`
 * outright with `0px 0px 0px 1px #e0e0e0` (`react-ui/src/theme/theme.ts`,
 * the `shadows` override) — a flat grey **hairline ring**, not a drop shadow.
 * So this is not "elevation 8, lighter": borrowing the menus' paper here put a
 * three-layer drop shadow under a paper the donor draws with a 1px outline
 * (#494, item 4). Its own constant, and the menus' generic one retired with
 * it — the autocomplete was the only caller, and a shared name over a value
 * one side does not want is how the shadow got there.
 *
 * **The 500px cap is a named parity break, and the owner asked for it.**
 * The donor has no cap: `AutocompleteListbox` is where MUI's
 * `maxHeight: '40vh'` lives (`Autocomplete.js:343-344`), and the donor replaces
 * that slot (`AutocompletePaginated.tsx:106` -> `listbox.tsx:11`), so the rule
 * never emits — the paper measures `max-height: none` on the running donor and
 * grows to 2464px for the full manufacturer list, running off the bottom of the
 * viewport with no reachable end.
 *
 * That behaviour did ship here briefly. #542's decision round ruled the cap off
 * to match it (**D1 = b**, 2026-09-22); the owner then tested the result on the
 * running Atlas catalog the same day, called the panel buggy, and **reversed
 * that ruling**: the panel takes a fixed height and scrolls, "similar to the
 * Category filter", and it is "an approved divergence from Modsy". So this is
 * the later of two owner rulings on the same question, and it is the one that
 * binds. 500px is not a fresh number — it is `MUI_MENU_PANEL_MAX_HEIGHT`, the
 * cap the donor sets on its own category menu, and taking *that* rather than
 * the number is what "similar to the Category filter" means here.
 *
 * `overflow-y-auto` is not Atlas's either way: `AutocompletePaper` itself is
 * `overflow: 'auto'` (`Autocomplete.js:306-314`), a slot the donor does *not*
 * replace. Uncapped it never produced a scrollbar; it is the cap above that
 * puts the pair to work.
 */
export const MUI_AUTOCOMPLETE_PANEL = cn(
  MUI_PAPER_SHAPE,
  // **`border-0` before the ring, or there are two grey lines.** The vendored
  // `PopoverContent` carries a bare `border` (`popover.tsx:27`) and the ring
  // does not displace it — a `box-shadow` spread paints *outside* the border
  // box, the border inside — so the panel wore a 2px rim where the donor
  // draws 1px, and every option started 1px in from the paper's left edge.
  // `AutocompletePaper` is `border-width: 0` on the running donor: the
  // hairline is the whole treatment (#542's parity review).
  "border-0",
  "shadow-[0px_0px_0px_1px_var(--color-lennar-grey-300)]",
  // **And no entry animation**, which is the third vendored `PopoverContent`
  // default this panel has had to turn off. The donor's popper takes none:
  // `Autocomplete.js:599-616` builds the slot with `disablePortal`, a width
  // and an `anchorEl` and nothing else, and `Popper` defaults `transition` to
  // `false` — measured on the running donor, the paper's `animation-name` is
  // `none`. Atlas's list faded up from 95% and 8px above on every open.
  //
  // **Both states, and both `!`** — and the `!` is the whole thing. The
  // vendored classes are `data-[state=open]:animate-in` and
  // `data-[state=closed]:animate-out`, so matching the variant gives these
  // the *same* selector and the same (0,2,0) specificity. Nothing breaks the
  // tie but source order, and Tailwind v4 emits alphabetically within a
  // variant: `animate-none` beats `animate-in` and loses to `animate-out`.
  // Without the `!`, only the state whose spelling happens to sort later is
  // fixed and the other still animates. tailwind-merge drops neither pair;
  // both survive in the DOM. An important author declaration wins regardless
  // of emission order.
  //
  // Only here — `MUI_MENU_SURFACE` keeps the vendored entrance. That is a
  // scope decision, not a parity claim: MUI's `Menu` papers do animate, but
  // on `Grow` (scale 0.75→1 over ~357ms, origin at the anchor), which is not
  // what the vendored `zoom-in-95` over 150ms draws either. The menus' own
  // entrance is pre-existing on `main` and outside this ticket.
  "data-[state=open]:animate-none! data-[state=closed]:animate-none!",
  MUI_MENU_PANEL_MAX_HEIGHT,
  "w-[var(--radix-popover-trigger-width)] overflow-y-auto",
);

/**
 * **A long label truncates; it never wraps.** `InputLabel`'s root is
 * `white-space: nowrap; overflow: hidden; text-overflow: ellipsis`
 * (`InputLabel.js:57-65`), with a `max-width` stated per state beside each
 * transform — so a label wider than its field ellipsizes on one line.
 *
 * The budgets, and why Atlas's numbers differ from MUI's written ones. MUI
 * declares `calc(100% - 24px)` resting (`:153`) and `calc(133% - 32px)` shrunk
 * (`:176`) — the second on a box it then scales by 0.75, hence `133%`, and
 * hence `133% * 0.75` here, where Atlas declares the scaled size directly.
 * MUI's `32` is deliberately *not* the exact `(8+5)*2/0.75 = 34` — its own
 * comment says 32 "feels better when it bleeds a bit on the left" — so it rides
 * as written rather than re-derived. The shrunk box then gives back the 10px of
 * notch padding MUI keeps on its legend instead of its label, leaving the same
 * rendered text budget.
 *
 * `overflow-hidden` is the label's own clipping, and nothing else's: the notch
 * is a sibling element on the frame (`FieldNotch`), so the label no longer
 * carries the gap it sits in.
 */
const MUI_FIELD_LABEL_ELLIPSIS = "overflow-hidden text-ellipsis whitespace-nowrap";

/**
 * **The shrunk outlined label's box — the one home for that measurement.**
 *
 * MUI puts a shrunk outlined label at `translate(14px, -9px) scale(0.75)`,
 * origin `top left`, on a box that is `font-size: 16px` with
 * `line-height: 1.4375em` hard-coded in `FormLabel.js:51-52` (over `body1`'s
 * 1.5). Scaled, that is a 12px-text box with a **17.25px** line box whose
 * top-left lands at (14, −9) — and `InputLabel.js:165-178` keys only on
 * `variant` and `shrink`, with **no small-size variant**, so a 40px field
 * shrinks to exactly the same place as a 56px one. Hence one constant, shared
 * by `OutlinedField` (the 56px form frame) and by `MUI_FIELD_LABEL` below (the
 * 40px popover-backed controls).
 *
 * Atlas declares the scaled values instead of scaling, so each number is read
 * straight off the source:
 *
 *   - `-top-[9px]` — MUI's translateY, verbatim;
 *   - `text-xs leading-[1.4375]` — 16 × 0.75 on the same 1.4375 multiplier, so
 *     12 × 1.4375 = 17.25px, MUI's scaled line box to the digit. **The
 *     `leading-*` must follow the `text-*`**: `tailwind-merge` treats the two as
 *     conflicting (v3's `text-xs` bundled a line-height), so a `leading`
 *     declared first is dropped and the label silently keeps `text-xs`'s 16px
 *     box — which is what it carried here before #492;
 *   - `left-[9px] px-[5px]` — MUI's two horizontal numbers, not one. The
 *     label's *text* rides at 14px, while the notch hole starts at 9px: the
 *     fieldset's 1px border plus its `padding: 0 8px`
 *     (`NotchedOutline.js:23,27`), with the legend carrying 5px either side of
 *     the text (`NotchedOutline.js:79`). Atlas paints the notch with the
 *     label's own background rather than an SVG legend, so the 5px is padding
 *     and the 9px is where the box starts.
 *
 * The drift this consolidation ends: the same fact lived in two files with two
 * different answers — `-top-2 left-3.5 px-1 leading-none` on the form frame,
 * and `-top-2 left-2 px-1` with no `leading` at all here, which kept
 * `text-xs`'s 16px box and so landed within 0.4px of MUI *by accident*. Geometry
 * only, deliberately: the colour and the notch's background are the caller's,
 * because `OutlinedField` lets a caller on a tinted surface say what is painted
 * behind the label.
 */
export const MUI_SHRUNK_FIELD_LABEL_BOX =
  "-top-[9px] left-[9px] px-[5px] text-xs leading-[1.4375] " +
  MUI_FIELD_LABEL_ELLIPSIS +
  " max-w-[calc(133%*0.75_-_14px)]";

/**
 * **MUI's `NotchedOutline` root** — the `<fieldset>` that draws an outlined
 * field's line, with the label's hole cut out of it (`FieldNotch`, #846).
 *
 * `NotchedOutline.js:16-34`, declaration for declaration: absolute at `top:
 * -5px` and flush on the other three sides, `margin: 0`, `padding: 0 8px`, a
 * solid border, `overflow: hidden`, `min-width: 0%`, `text-align: left`, and
 * `pointer-events: none` so every press lands on the control beneath. The
 * radius is MUI's `inherit` of its root's 4px, written out because a trigger's
 * root here is a plain positioned box with no radius of its own.
 *
 * **The `-5px` is not decoration.** A fieldset centres its top border on its
 * legend, so with an 11px legend the line lands `(11 − width) / 2` below the
 * fieldset's top: exactly on the field's top edge at 1px, and half a pixel
 * above it at the focused 2px — which is where MUI's focused line is, and
 * which two rows it covers at a fractional y.
 *
 * **The colour and width are the surface's**, read from two custom properties
 * so that one paint rule serves every surface however its state is expressed:
 * `OutlinedField` sets them on its frame (this fieldset's ancestor), the
 * popover-backed controls set them on the fieldset itself from their control
 * through `MUI_FIELD_OUTLINE_PEER`. The fallbacks are MUI's resting line.
 *
 * Out of flow, as MUI's is, so a 56px field measures 56.
 */
export const MUI_FIELD_OUTLINE = cn(
  "pointer-events-none absolute inset-x-0 -top-[5px] bottom-0 m-0 min-w-0 overflow-hidden rounded-[4px] px-2 text-left",
  "border-solid border-(--mui-outline-color,var(--color-lennar-field-outline)) [border-width:var(--mui-outline-width,1px)]",
);

/**
 * **MUI's `NotchedOutline` legend** — the hole itself.
 *
 * `NotchedOutline.js:35-84`: `float: unset`, `width: auto`, `display: block`,
 * `padding: 0`, an explicit `height: 11px`, `font-size: 0.75em` (12px off the
 * field's 16px; `text-xs` here), `visibility: hidden`, `overflow: hidden` and
 * `white-space: nowrap`. Sized by its content, then capped — `max-width` is the
 * one property that animates (`MUI_FIELD_NOTCH_OPEN`/`_CLOSED`), and
 * `overflow-hidden whitespace-nowrap` is what makes the cap *clip* the copied
 * label rather than reflow it.
 *
 * It paints nothing, so it can reach above the field without the hazard #561's
 * D2 closed: that was a painted band clipping a paragraph's descenders. A
 * legend's only effect is the border it displaces.
 *
 * **The 5px is deliberately not here.** MUI keeps `padding: 0` on the legend
 * and puts `padding: 0 5px` on the span inside it (`NotchedOutline.js:66,79`),
 * and that split is load-bearing rather than incidental: the legend is the box
 * `max-width` caps, and under `box-sizing: border-box` a box cannot be capped
 * below its own padding. With the 5px here, a closed notch floored at 10px
 * instead of MUI's 0.01px and stayed visible as a permanent hole in the border.
 * `FieldNotch` carries the inner span.
 */
export const MUI_FIELD_NOTCH_LEGEND =
  "invisible float-none block h-[11px] w-auto overflow-hidden p-0 text-xs whitespace-nowrap";

/**
 * **The hole, open** — and the clock it opened on.
 *
 * `max-width: 100%` with `transition: max-width 100ms easeOut 50ms`
 * (`NotchedOutline.js:85-97`). The delay is what lets the label start rising
 * before the border gives way, and the 100ms finishes 50ms before the label's
 * own 200ms does (`MUI_FIELD_LABEL_TRANSITION`) — so a label crossing an
 * intact border for the first fifty milliseconds is the donor's look, not a
 * mistimed port.
 *
 * MUI's `100%` is its **fieldset's content box**: the frame less its border
 * either side and its `padding: 0 8px` (`NotchedOutline.js:23,27`), so the hole
 * stops as far short of the frame's right edge as it starts from its left.
 * Atlas's legend sits in the same fieldset (`MUI_FIELD_OUTLINE`), so its
 * `100%` is that box too — and when focus takes the line to 2px, the box
 * shrinks by a pixel either side while the declared `100%` stays `100%`. A
 * transition fires on a computed-value change, so the gap's ends jump to 10px
 * exactly as the donor's do, with nothing animating.
 */
export const MUI_FIELD_NOTCH_OPEN =
  "max-w-full transition-[max-width] delay-50 duration-100 ease-out";

/**
 * **The hole, closed** — and the shorter clock it closes on.
 *
 * `max-width: 0.01px` with `transition: max-width 50ms easeOut` and **no
 * delay** (`NotchedOutline.js:73-84`). MUI declares a transition on each of its
 * two legend states rather than one on the element, so the furl is genuinely
 * not the unfurl reversed: half the duration, and it starts at once. The
 * ticket's "furls symmetrically" is the behaviour, not the numbers — Atlas had
 * no furl at all, because the band only existed in the shrunk class.
 *
 * `0.01px` is MUI's own value, carried rather than rounded to zero.
 */
export const MUI_FIELD_NOTCH_CLOSED = "max-w-[0.01px] transition-[max-width] duration-50 ease-out";

/**
 * **A floated label stands down while its own field is being pressed.**
 *
 * MUI's shrunk `InputLabel` is `pointer-events: auto`
 * (`InputLabel.js:165-172`) so that a click on it is swallowed rather than
 * falling through to the control underneath, and the ports keep that. What the
 * ports cannot keep for free is *when* the label arrives over the pointer.
 *
 * MUI opens a `Select` on **mousedown** (`SelectInput.js`'s `handleMouseDown`),
 * so by the time its label starts the 200ms climb the menu is already up and a
 * modal backdrop is over the trigger. Radix's `PopoverTrigger` opens on
 * **click**. Shrink the label on focus — which is what MUI's `notched` and
 * `shrink` actually key off — and the sequence becomes: mousedown focuses the
 * trigger, the label turns `pointer-events: auto` and sets off across the
 * field, and 200ms later it is still mid-travel over the press. The mouseup
 * lands on the *label*, so the click resolves to the common ancestor and the
 * trigger never hears it: **pressing the middle of a select stopped opening
 * it.**
 *
 * So the label ignores the pointer for as long as the control beside it is
 * being pressed. It gives up nothing the donor shows — a press on the field is
 * a press on the field in both — and it is deliberately keyed on the *control*
 * rather than on the group, because `:active` also applies to the ancestors of
 * whatever is pressed: `group-active:` would have stood the label down when the
 * label itself was pressed, which is the one case its `pointer-events: auto`
 * exists for.
 *
 * Both spellings in one literal because Tailwind v4 scans source text, and the
 * three surfaces that need it hold two kinds of control: a `<button>` trigger
 * (`tree-select`, `multi-select-field`) and an `<input>` (`autocomplete-field`).
 */
export const MUI_FIELD_LABEL_PRESS_STAND_DOWN =
  "group-has-[button:active]:pointer-events-none group-has-[input:active]:pointer-events-none";

/**
 * MUI's **shrunk** outlined label: the small caption notched into the field's
 * top border. The gap it sits in is **not drawn here** — it is `FieldNotch`, an
 * element on the frame, because a gap that rides the label can be neither
 * capped nor held still while the label travels (#579).
 *
 * Shrunk is only half of MUI's behaviour. An outlined label starts *inside* the
 * field, at input size, and rises to this position when the field is focused or
 * holds something — `MUI_FIELD_LABEL_RESTING` is where it starts. Which of the
 * two a control uses is not a style choice:
 *
 *   - the category select forces the shrunk one always, because the donor
 *     passes `shrink={... || allowEmpty}` and it always shows a value
 *     ("All Categories" when nothing is picked) — `CategoriesFieldPaginated.tsx:259`;
 *   - the manufacturer autocomplete gets MUI's default, and it holds no value
 *     at all (it is a picker — each pick becomes a chip), so its label sits
 *     inside the field until the user focuses it or types.
 */
export const MUI_FIELD_LABEL = cn(
  // `pointer-events: auto` and `userSelect: 'none'` — MUI's shrunk variant
  // overriding the outlined base's `pointer-events: none`
  // (`InputLabel.js:153,165-172`). It matters most here: with the label a hole,
  // a click on it fell through to the trigger underneath and opened the menu,
  // where the donor's label swallows it.
  "text-lennar-ink-secondary pointer-events-auto absolute z-10 select-none",
  MUI_SHRUNK_FIELD_LABEL_BOX,
);

/**
 * The resting outlined label's box: inside the field, at the input's size.
 *
 * **A fixed offset from the field's top border, and MUI has two of them.**
 * `translate(14px, 16px)` on a medium field, `translate(14px, 9px)` on a small
 * one (`InputLabel.js:146-164`) — resting position tracks the field's height,
 * where the shrunk position does not (`MUI_SHRUNK_FIELD_LABEL_BOX`).
 *
 * It was centred here (`top-1/2 -translate-y-1/2`) on the argument that one
 * size-agnostic rule lands within 0.5px of both transforms. The arithmetic was
 * right and the premise was wrong twice, and #492's sweep is what caught it:
 *
 *   - centring is only within 0.5px of MUI when the offset parent is the field
 *     itself. In all three consumers the `relative` wrapper also holds the
 *     error/helper `<p>` (`multi-select-field.tsx:192,334-349`,
 *     `tree-select.tsx:308,468-473`), so the centre found was the centre of
 *     field-*plus-message* and the label dropped 10px the moment one rendered
 *     — on the Request Item dialog's Category after an empty submit, and on the
 *     item edit page's Level for as long as the tier vocabulary is in flight;
 *   - the consumers are not all small. `MultiSelectField` defaults to
 *     `MUI_FIELD_NOTCHED_TRIGGER_MEDIUM` and `TreeSelect` takes 56px at
 *     `size="medium"` (`:356`), which is what that dialog's Category is.
 *
 * So: MUI's two numbers, chosen by the consumer's own size, the way it already
 * chooses its trigger height. `OutlinedField` is always medium (`min-h-14`) and
 * composes the medium box. The vertical offset is the *only* thing the two
 * sizes disagree on, so it is the only thing written twice.
 *
 * `leading-[1.4375]` for the same reason it is on the shrunk box: it is
 * `FormLabel`'s, it is state-independent, and `text-base`'s bundled 24px line
 * box is a pixel taller than MUI's 23. It follows `text-base` because
 * `tailwind-merge` drops a `leading-*` that precedes a conflicting `text-*`.
 */
const MUI_RESTING_FIELD_LABEL_BOX_TAIL =
  "left-3.5 text-base leading-[1.4375] " + MUI_FIELD_LABEL_ELLIPSIS + " max-w-[calc(100%_-_24px)]";

export const MUI_RESTING_FIELD_LABEL_BOX = "top-4 " + MUI_RESTING_FIELD_LABEL_BOX_TAIL;

/** `MUI_RESTING_FIELD_LABEL_BOX` for a 40px field: `translate(14px, 9px)`. */
export const MUI_RESTING_FIELD_LABEL_BOX_SMALL = "top-[9px] " + MUI_RESTING_FIELD_LABEL_BOX_TAIL;

/** The resting label on a 56px field — see `MUI_RESTING_FIELD_LABEL_BOX`. */
export const MUI_FIELD_LABEL_RESTING = cn(
  "text-lennar-ink-secondary pointer-events-none absolute z-10",
  MUI_RESTING_FIELD_LABEL_BOX,
);

/** The resting label on a 40px field — see `MUI_RESTING_FIELD_LABEL_BOX`. */
export const MUI_FIELD_LABEL_RESTING_SMALL = cn(
  "text-lennar-ink-secondary pointer-events-none absolute z-10",
  MUI_RESTING_FIELD_LABEL_BOX_SMALL,
);

/**
 * The clock the label moves on, between its two positions.
 *
 * Every donor `InputLabel` animates `color`, `transform` and `max-width` over
 * `duration.shorter` on `easing.easeOut` (`InputLabel.js:94-103`), and the
 * donor theme overrides `transitions` nowhere — so those are MUI's own 200ms
 * and `cubic-bezier(0.0, 0, 0.2, 1)` (`createTransitions.js:18,29`), which is
 * Tailwind's `ease-out` to the digit. `disableAnimation` appears nowhere in
 * `timmy/src` or `timmy/packages`, so it is live on all of them.
 *
 * Atlas animates `top`/`left`/`font-size`/`padding` instead, because declaring
 * the scaled values is what it does in place of scaling — but `left` and the
 * notch padding move in opposite directions by the same 5px, so the label's
 * *text* holds still at 14px for the whole transition, which is exactly what
 * MUI's constant `translate(14px, …)` does.
 *
 * Shared, because it was not: three of the four label surfaces ran Tailwind's
 * default 150ms curve or had no transition at all while `OutlinedField` ran the
 * donor's — one geometry, two clocks, in the same app.
 */
export const MUI_FIELD_LABEL_TRANSITION = "transition-all duration-200 ease-out";

/**
 * MUI's `FormHelperText`: the error or helper line under a field.
 *
 * `FormHelperText` spreads `theme.typography.caption` and adds `marginTop: 3`
 * (`FormHelperText.js:49-51`); its `contained` variant adds `marginLeft` and
 * `marginRight: 14` (`:70-78`), and `contained` is true for every field here —
 * it reads `variant === 'filled' || 'outlined'` off the `FormControl` context,
 * `FormControl` defaults `variant='outlined'` (`FormControl.js:121`), and no
 * donor consumer passes one. `textAlign: 'left'` is on the root.
 *
 * **The line box is 19.92px, not 16.** `caption` is
 * `buildVariant(fontWeightRegular, 12, 1.66, 0.4)` (`createTypography.js:75`),
 * and the donor theme overrides only its `fontSize` (`theme.ts:185-187`), which
 * `deepmerge` applies without disturbing the 1.66 — so 12 × 1.66. Tailwind's
 * `text-xs` bundles a 16px line box instead, which was 3.92px short on every
 * message in the app; on a form that shows several errors at once the shortfall
 * accumulates down the column, which is the same drift #492 exists to remove.
 * `leading-[1.66]` follows the size because `tailwind-merge` drops a
 * `leading-*` that precedes a conflicting `text-*`.
 *
 * The fourth argument to `buildVariant` is `letterSpacing`, and it produces
 * none here: MUI builds a variant's tracking only under its own Roboto default
 * (`createTypography.js:56-58`) and the donor sets Inter.
 *
 * Shared because it was derived once, on `image-upload-field.tsx`'s hand-rolled
 * error, and the frame every other field wears never got it.
 *
 * Carries `mt-[3px]`; a caller wanting MUI's other spacing overrides it (see
 * `OutlinedField`'s `messageSpacing`), which `tailwind-merge` resolves.
 */
export const MUI_FIELD_HELPER_TEXT = "mx-3.5 mt-[3px] text-left text-xs leading-[1.66] font-normal";

/** MUI tints the label with the focus colour while the field is focused. */
export const MUI_FIELD_LABEL_FOCUSED = "text-lennar-blue-600";

/**
 * MUI's `MenuItem`: **36px**, edge to edge, 16px text, tinted when current.
 *
 * 36, not 48. MUI sets `minHeight: 48` on a menu row only below the `sm`
 * breakpoint — a touch target on a phone — and `minHeight: auto` above it, where
 * the row is its `padding: 6px 16px` around a 24px line. This constant carried
 * 48 at every width, which put every ported menu a third taller than the
 * source's; measured on the running donor, its Status and lookup menus are 36.
 *
 * The multi-select's rows are legitimately taller and stay so: they hold a
 * checkbox with its own hit box, which is what makes those rows 54 in both.
 */
export const MUI_MENU_ITEM = cn(
  // `relative` so a row's ripple layer — `absolute inset-0` — resolves against
  // the row. No `overflow-hidden`: the layer clips itself.
  "relative",
  "text-lennar-ink flex min-h-9 w-full items-center rounded-none px-4 py-1.5 text-left text-base",
  // `:hover` is `action.hover`; `.Mui-focusVisible` is `action.focus`, which is
  // three times as strong (`MenuItem.js:76-99`) — they are not the same tint.
  "hover:bg-lennar-action-hover focus-visible:bg-lennar-action-focus focus-visible:outline-none",
);

/**
 * The tint MUI marks the current row with, in place of a tick.
 *
 * Usable only where the tint is unconditional in the markup — a *variant* of it
 * (`data-[state=checked]:…`) has to be spelled out at its call site, because
 * Tailwind v4 scans source text and a prefix interpolated onto this constant
 * generates no CSS. `select-field.tsx` is that case and says so.
 */
export const MUI_MENU_ITEM_SELECTED = "bg-lennar-primary-selected";

/**
 * An `Autocomplete` option row — which is **not** a `MenuItem`.
 *
 * The donor replaces the listbox slot with a plain MUI `<List>`
 * (`AutocompletePaginated.tsx:106` → `listbox.tsx:11`), so `useSlot` takes
 * `AutocompleteListbox` out of the tree and every `.MuiAutocomplete-option`
 * rule nested inside it — `minHeight: 48`, `padding: 6px 16px`,
 * `display: flex`, and the `.Mui-focused` / `[aria-selected]` tints
 * (`Autocomplete.js:342-389`) — never emits. What is left is a bare `<li>`:
 * `display: list-item`, no padding, and 24px of height from the paper's
 * 16px/1.5 body text, with the label flush against the paper's left edge.
 *
 * `MUI_MENU_ITEM` is the wrong shape here and drawing it whole was visible: a
 * flex row made the donor's unstyled list read as a menu (#494 item 1).
 *
 * **The row's padding is a named parity break, and the owner asked for it.**
 * The donor's row really is flush to the paper's left edge, and that shipped;
 * on 2026-09-22 the owner tested the panel on the running Atlas catalog and
 * ruled that it take "some padding similar to Category filter" as "an approved
 * divergence from Modsy", alongside the cap on `MUI_AUTOCOMPLETE_PANEL`. So
 * `px-4 py-1.5` is borrowed from `MUI_MENU_ITEM` — the Category filter's own
 * row padding — and only the padding is: the row keeps `display: list-item`
 * and takes its height from the text plus the band, rather than putting on a
 * `MenuItem`'s flex box and `min-height`. 24px of text and 12px of padding is
 * 36px, which is where `MUI_MENU_ITEM`'s `min-h-9` lands anyway.
 *
 * Written out rather than extracted the way the cap above is, and that is a
 * decision: `MUI_MENU_ITEM` holds its padding inside one long class string
 * whose order `cn` resolves, so hoisting two tokens out of the middle of it
 * moves what wins a merge. The cap was one token in its own segment and cost
 * nothing. If a third row ever wants this padding, extract it then.
 *
 * **Three affordances here are Atlas's and are not the donor's**, and they
 * are one finding rather than three: the same replaced slot that took the
 * padding away took every *interactive* rule with it. The donor's rows have
 * no hover tint, no keyboard-active tint, and no `cursor: pointer` — a bare
 * `<li>` leaves the arrow cursor on a row that is, in fact, clickable. Atlas
 * keeps all three: the `hover:bg-lennar-action-hover` below, and — at the
 * call site, because they are conditional and Tailwind v4 scans source text —
 * `MUI_MENU_ITEM_SELECTED` on the active index and `cursor-pointer`
 * (`autocomplete-field.tsx`).
 *
 * Keeping them is one named parity break, and the **owner ruled it kept** on
 * #542's decision round (**D3 = a**, 2026-09-22): all three stay. They were
 * put to the owner as one question rather than three, because ruling them
 * apart would leave a row that tints under the pointer and does not admit it
 * is clickable; they were separated from the geometry above so the row's
 * measurements did not ride along with them.
 *
 * This is a licence for these three affordances on this row, not a general
 * one — a further affordance the donor does not draw is a fresh break and a
 * fresh ruling.
 */
export const MUI_AUTOCOMPLETE_OPTION = cn(
  "text-lennar-ink w-full text-base leading-6",
  "px-4 py-1.5",
  "hover:bg-lennar-action-hover",
);

/**
 * Radix's select menu, wearing MUI's paper.
 *
 * Its own constant rather than a class list in each select, because there are
 * two selects: `select-field.tsx` (the toolbars' `size='small'`) and
 * `outlined-select-field.tsx` (the forms' default 56px). They differ in the
 * *trigger* and agree completely about the menu — so the menu lives here, where
 * a change to what a dropdown looks like lands on both at once.
 */
export const MUI_SELECT_MENU = cn(
  MUI_MENU_SURFACE,
  // Radix's select names its own width variable, and hangs the list padding on
  // an inner viewport shadcn insets by 4px — which stops the hover highlight
  // short of the menu's own sides.
  "min-w-[var(--radix-select-trigger-width)] py-0",
  "[&_[data-radix-select-viewport]]:p-0 [&_[data-radix-select-viewport]]:py-2",
  // shadcn nudges a popper-positioned menu 4px off its trigger on every side.
  // MUI's `Select` menu has no offset at all (`SelectInput.js:772-779`) — it
  // hangs off the field — so `sideOffset={0}` alone does not get there; the
  // translate has to go too.
  "data-[side=bottom]:translate-y-0 data-[side=left]:translate-x-0 data-[side=right]:translate-x-0 data-[side=top]:translate-y-0",
);

/** One row in that menu. */
export const MUI_SELECT_MENU_ITEM = cn(
  MUI_MENU_ITEM,
  // Spelled out rather than composed from `MUI_MENU_ITEM_SELECTED`: Tailwind v4
  // scans source text, so a class built by interpolation generates no CSS.
  "data-[state=checked]:bg-lennar-primary-selected",
  // No tick: MUI marks the current row with a tint, and the tick's gutter is
  // what makes the shadcn menu sit narrower than its own trigger.
  "pr-4 [&>[data-slot=select-item-indicator]]:hidden",
  "focus:bg-lennar-action-hover",
);
